import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { parseISO, isValid, format } from 'date-fns';
import { submitVerificationResult } from '../../../services/admin/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
 
const ResultOfVerificationSection = ({ candidateId, verifierData }) => {
  const options = [
    { label: 'Select result of verification', value: '', disabled: true },
    { label: 'Discrepant', value: 'DISCREPANT' },
    { label: 'Minor Discrepant', value: 'MINOR_DISCREPANT' },
    { label: 'Attention Required', value: 'ATTENTION_REQUIRED' },
    { label: 'Insufficient', value: 'INSUFFICIENT' },
    { label: 'No Response Received', value: 'NO_RESPONSE_RECEIVED' },
    { label: 'Clear', value: 'CLEAR' },
  ];
 
  const [selectedStatus, setSelectedStatus] = useState('');
  const [reportDate, setReportDate] = useState(null);
  const [verifierName, setVerifierName] = useState('');
  const [verifierEmail, setVerifierEmail] = useState('');
  const [verifierEmployeeCode, setVerifierEmployeeCode] = useState('');
  const [verifierMobileNumber, setVerifierMobileNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
 
  useEffect(() => {
    const localName = localStorage.getItem('user_name') || '';
    const localEmail = localStorage.getItem('user_email') || '';
 
    setVerifierName(verifierData?.verifierName || localName);
    setVerifierEmail(verifierData?.verifierEmail || localEmail);
    setVerifierEmployeeCode(verifierData?.verifierEmployeeCode || '');
    setVerifierMobileNumber(verifierData?.verifierMobileNumber || '');
 
    if (verifierData?.reportDate) {
      let parsedDate = null;
      const dateParts = verifierData.reportDate.split('-');
      if (dateParts.length === 3) {
        const [day, month, year] = dateParts.map(Number);
        const d = new Date(year, month - 1, day);
        if (isValid(d)) parsedDate = d;
      }
      if (!parsedDate) {
        try {
          const isoParsed = parseISO(verifierData.reportDate);
          if (isValid(isoParsed)) parsedDate = isoParsed;
        } catch (e) {}
      }
      setReportDate(parsedDate);
    } else {
      setReportDate(null);
    }
  }, [verifierData]);
 
  const handleDateChange = (date) => setReportDate(date);
  const handleVerificationResultChange = (e) => setSelectedStatus(e.target.value);
 
  const handleSubmit = async () => {
    if (!selectedStatus) {
      toast.error('⚠️ Please select a valid verification status before submitting.', {
        position: 'top-right',
      });
      return;
    }
 
    const payload = {
      candidateId,
      verificationStatus: selectedStatus,
      reportDate: reportDate ? format(reportDate, 'dd-MM-yyyy') : null,
      verifierName,
      verifierEmail,
    };
 
    //console.log('📦 Payload being submitted:', payload);
 
    setLoading(true);
 
    // ✅ Show success immediately
    toast.success(`Verification result submitted successfully for candidate ${candidateId}.`, {
      position: 'top-right',
      autoClose: 2000,
    });
 
    // ✅ Call API in background (not blocking navigation)
    submitVerificationResult(payload).catch(() => {
      console.error('⚠️ Backend submission failed, but UI already navigated.');
    });
 
    // ✅ Navigate back after 3 seconds regardless of API result
    setTimeout(() => {
      setLoading(false);
      navigate(-1);
    }, 3000);
  };
 
  const handleCancel = () => navigate(-1);
 
  return (
    <div className="report-section">
      <h2 className="section-title">Verifier Details</h2>
 
      <div className="grid-container">
        <div className="info-field">
          <label className="info-label">Verifier Name</label>
          <div className="info-value">{verifierName}</div>
        </div>
 
        <div className="info-field">
          <label className="info-label">Verifier Email</label>
          <div className="info-value">{verifierEmail}</div>
        </div>
 
        <div className="info-field">
          <label className="info-label">Report Date</label>
          <div className="input-container">
            <DatePicker
              selected={reportDate}
              onChange={handleDateChange}
              dateFormat="dd-MM-yyyy"
              className="form-input-date"
              placeholderText="dd-mm-yyyy"
              isClearable
            />
          </div>
        </div>
 
        <div className="info-field">
          <label className="info-label">Result Of Verification</label>
          <select
            className="select-box"
            value={selectedStatus}
            onChange={handleVerificationResultChange}
          >
            {options.map((option, index) => (
              <option key={index} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
 
      <div className="btn-group">
        <button className="btn cancel" onClick={handleCancel} disabled={loading}>
          Cancel
        </button>
        <button className="btn submit" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Submitting...' : 'Submit'}
        </button>
      </div>
 
      {loading && (
        <div className="loader-overlay">
          <div className="loader"></div>
        </div>
      )}
 
      <style>{`
        .loader-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.6);
          z-index: 9999;
        }
        .loader {
          border: 6px solid #f3f3f3;
          border-top: 6px solid #3498db;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
 
export default ResultOfVerificationSection;